import { createRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { QueryDropdown } from 'basic-components';
import { CommonService } from 'dashboard-services/services';
import { DropdownService } from 'primary-components';

import getApiConfig from 'api/ApiConfig';
import properties from 'resources/constants/properties.json';

import { useQueryClient } from '@tanstack/react-query';

const EMPTY_OBJ = {};

const getGroupName = value => {
  if(value?.groupName) {
    return value.groupName
  }
  if(CommonService.isJson(value)) {
    const parsed = JSON.parse(value)
    return parsed?.groupName || undefined
  }
  return value;
}

const getPropByKey = (value, propKey) => {
  const valueParsed = CommonService.isJson(value) ? JSON.parse(value) : value;
  if(valueParsed?.[propKey]) {
    const newProp = {}
    Object.entries(valueParsed?.[propKey] || {}).forEach(([k, v]) => {
      if(typeof v !== "string" && v.length > 0) {
        newProp[k] = v[0]
      } else {
        newProp[k] = v
      }
    })
    return newProp
  }
  return EMPTY_OBJ;
}

const ALL = { label: "All", value: "*" }
const EMPTY_ARRAY = [];
const SymbolColumn = ({
  onChange,
  value,
  name,
  dependsOnParam = EMPTY_OBJ,
  disabled,
  isMulti = EMPTY_OBJ,
  size = undefined,
  menuPortalTarget = undefined,
  ref,
  ...props
}) => {
  const dispatch = useDispatch(),
        [columnsQuery, setColumnsQuery] = useState(),
        queryClient = useQueryClient(),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        columnsRaw = useMemo(() => {
          if(isMulti) {
            return value !== "" ? String(value).split(',') : EMPTY_ARRAY
          }
          return value !== "" ? String(value) : undefined;
        }, [isMulti, value]),
        groupName = useMemo(() => getGroupName(dependsOnParam?.value || dependsOnParam?.defaultValue), [dependsOnParam]),
        symbol = useMemo(() => getPropByKey(dependsOnParam?.value || dependsOnParam?.defaultValue, "symbol"), [dependsOnParam]),
        metadata = useMemo(() => getPropByKey(dependsOnParam?.value || dependsOnParam?.defaultValue, "metadata"), [dependsOnParam]),
        withMetadata = useMemo(() => Object.entries(metadata || {})?.length > 0, [metadata]),
        { fetchNextPage: fetchMoreColumns, isFetching: isFetchingColumns, isFetchingNextPage: isFetchingNextPageColumns, data: columnsFromApiRaw } = DropdownService.useQueryColumns({
          queryClient,
          input: columnsQuery,
          groupName,
          useEs: properties.useEsSymbolListing === "true",
          getApiConfig: getApiConfigLocal,
          symbols: symbol,
          ...(withMetadata ? { metadata: true, metadatas: metadata } : {})
        }),
        columnsFromApi = useMemo(() => [ALL].concat(columnsFromApiRaw || []), [columnsFromApiRaw]),
        columnRef = useMemo(() => ref || createRef(), [ref]),
        columns = useMemo(() => isMulti ? columnsRaw?.map(c => c === ALL.value ? ALL : { label: c, value: c }) : columnsRaw, [columnsRaw, isMulti])

  useEffect(() => {
    columnRef.current?.refreshOptions?.()
  }, [columnRef, metadata, symbol, groupName])

  useImperativeHandle(ref, () => ({ 
    validateForForm: () => !!value,
    isValid: () => !!value
  }))

  return (
    <QueryDropdown
        {...props}
        defaultValue={columns}
        disabled={disabled}
        fetchNextPage={fetchMoreColumns}
        isFetching={isFetchingColumns || isFetchingNextPageColumns}
        menuPortalTarget={menuPortalTarget || document.body}
        multi={isMulti}
        name={name}
        onChange={onChange}
        onInputChange={setColumnsQuery}
        options={columnsFromApi}
        ref={columnRef}
        size={size}
    />
  )
}

SymbolColumn.displayName = "SymbolColumn"

SymbolColumn.propTypes = {
  dependsOnParam: PropTypes.object, 
  disabled: PropTypes.bool.isRequired, 
  isMulti: PropTypes.bool, 
  menuPortalTarget: PropTypes.any, 
  name: PropTypes.string.isRequired, 
  onChange: PropTypes.func.isRequired,
  size: PropTypes.string,
  value: PropTypes.any.isRequired
}

export default SymbolColumn;
